import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  MenuItem,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Tab,
  FormControlLabel,
  Switch,
  Divider,
  IconButton,
  Box,
  Stack
} from '@mui/material';
import * as React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SelectDefaultEdgeShape from '../Drawer/selectDefaultEdgeShape';
import SelectDefaultEdgeArrows from '../Drawer/selectDefaultEdgeArrows';
import { StyleShape } from '../../../types';
import { ElementPropertyNodeImage } from "./elementPropertyNodeImage";
import EditIcon from '@mui/icons-material/Edit';
import PaletteIcon from '@mui/icons-material/Palette';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import ColorPicker from '../../Controls/colorPicker';
import {useContext} from 'react';
import {ProjectContext} from '../../../contexts/ProjectContext';

const accordionStyles = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
  bgcolor: 'background.paper',
  '&::before': {
    display: 'none'
  }
};

const accordionDetailsStyles = {
  px: 3,
  py: 2
};

export const ElementPropertiesDialog = ({ 
  handleClose: onClose, 
  item, 
  type, 
  projectId, 
  open,
  setOpen 
}) => {
  const [elementDetails, setElementDetails] = React.useState();
  const [tabValue, setTabValue] = React.useState("1");
  const [expandedAppearance, setExpandedAppearance] = React.useState("panelAppearance1");
  const { updateElement, updateElementImage, projectId: contextProjectId, projectData: contextProjectData } = useContext(ProjectContext);
  
  // Use projectId from context if available, otherwise fall back to prop
  const effectiveProjectId = contextProjectId || projectId;

  // Memoized helper functions to avoid recalculations
  const getElementColor = React.useMemo(() => {
    return elementDetails?.data?.elementcolor ?? contextProjectData?.themeelementcolor;
  }, [elementDetails?.data?.elementcolor, contextProjectData?.themeelementcolor]);

  const getBorderColor = React.useMemo(() => {
    return elementDetails?.data?.bordercolor ?? contextProjectData?.themebordercolor;
  }, [elementDetails?.data?.bordercolor, contextProjectData?.themebordercolor]);

  const getBorderWidth = React.useMemo(() => {
    return elementDetails?.data?.borderwidth ?? contextProjectData?.themeborderwidth;
  }, [elementDetails?.data?.borderwidth, contextProjectData?.themeborderwidth]);

  const getTextColor = React.useMemo(() => {
    return elementDetails?.data?.textcolor ?? contextProjectData?.themetextcolor;
  }, [elementDetails?.data?.textcolor, contextProjectData?.themetextcolor]);

  const getTextBorderColor = React.useMemo(() => {
    return elementDetails?.data?.textbordercolor ?? contextProjectData?.themetextbordercolor;
  }, [elementDetails?.data?.textbordercolor, contextProjectData?.themetextbordercolor]);

  const getTextShowLabel = React.useMemo(() => {
    if (elementDetails?.data?.textshowlabel !== undefined) {
      return elementDetails?.data?.textshowlabel;  
    }
    else 
    return contextProjectData?.themetextshowlabel;
  }, [elementDetails?.data?.textshowlabel, contextProjectData?.themetextshowlabel]);


  const getTextSize = React.useMemo(() => {
    return elementDetails?.data?.textsize ?? contextProjectData?.themetextsize;
  }, [elementDetails?.data?.textsize, contextProjectData?.themetextsize]);

  const getTextBorderWidth = React.useMemo(() => {
    return elementDetails?.data?.textborderwidth ?? contextProjectData?.themetextborderwidth;
  }, [elementDetails?.data?.textborderwidth, contextProjectData?.themetextborderwidth]);

  const getEdgeColor = React.useMemo(() => {
    return elementDetails?.data?.edgecolor ?? contextProjectData?.themeedgecolor;
  }, [elementDetails?.data?.edgecolor, contextProjectData?.themeedgecolor]);

  const handleTabChange = React.useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleChange = React.useCallback((e) => {
    const { name, value, type: inputType, checked } = e.target;
    const parsedValue = inputType === 'checkbox'
      ? checked
      : inputType === 'number'
        ? (value === '' ? '' : Number(value))
        : value;
    setElementDetails(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [name]: parsedValue,
      }
    }));
  }, []);

  const handleChangeAppearance = React.useCallback((panel) => (event, isExpanded) => {
    setExpandedAppearance(isExpanded ? panel : false);
  }, []);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleSave = React.useCallback(() => {
    updateElement(elementDetails);
    setOpen(false);
  }, [updateElement, elementDetails, setOpen]);

  // Initialize element details when item prop changes
  React.useEffect(() => {
    if (item) {
      setElementDetails({ ...item });
    }
  }, [item]);

  // Memoized dialog props to prevent unnecessary re-renders
  const dialogProps = React.useMemo(() => ({
    sx: {
      minWidth: { xs: '90vw', sm: 700 },
      minHeight: 400,
      width: { xs: '90vw', sm: 'auto' },
      maxWidth: { xs: '90vw', sm: '40vw', md: '32rem' },
      maxHeight: { xs: '85vh', sm: '85vh' },
      borderRadius: 3,
      boxShadow: '0px 12px 40px rgba(15, 23, 42, 0.18)',
      overflow: 'hidden',
      backgroundImage: 'none',
    }
  }), []);

  // Memoized person fields for nodes
  const personFields = React.useMemo(() => [
    { name: 'firstname', label: 'First name' },
    { name: 'lastname', label: 'Last name' },
    { name: 'middlename', label: 'Middle name' },
    { name: 'company', label: 'Company' },
    { name: 'position', label: 'Position' }
  ], []);

  // Memoized custom fields
  const customFields = React.useMemo(() => [
    { name: 'customfield1', label: 'Custom field 1' },
    { name: 'customfield2', label: 'Custom field 2' },
    { name: 'customfield3', label: 'Custom field 3' }
  ], []);

  const subtitle = React.useMemo(() => {
    if (type === 'nodes') {
      return 'Update node attributes, appearance, and related content.';
    }
    if (type === 'edges') {
      return 'Adjust edge metadata and visual styling.';
    }
    return 'Edit element settings and styling.';
  }, [type]);

  const resolvedBorderWidth = elementDetails?.data?.borderwidth ?? getBorderWidth ?? '';
  const resolvedTextSize = elementDetails?.data?.textsize ?? getTextSize ?? '';
  const resolvedTextBorderWidth = elementDetails?.data?.textborderwidth ?? getTextBorderWidth ?? '';

  return (
    <Dialog 
      open={open}
      onClose={handleClose}
      PaperProps={dialogProps}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2.5,
          pr: 2,
          pl: 3
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            {elementDetails?.data?.name || 'Edit element'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close editor"
          onClick={handleClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent
        dividers
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          bgcolor: 'background.default'
        }}
      >
        <TabContext value={tabValue}>
          <TabList
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              '& .MuiTab-root': {
                minHeight: 48,
                px: 2.5,
                fontWeight: 500
              }
            }}
          >
            <Tab 
              label="Data" 
              iconPosition="start" 
              value="1" 
              icon={<EditIcon />} 
            />
            <Tab 
              label="Appearance" 
              iconPosition="start" 
              value="2" 
              icon={<PaletteIcon />} 
            />
          </TabList>
          
          <TabPanel value="1" sx={{ px: 0 }}>
            <Stack spacing={2.5}>
              <TextField 
                id="name" 
                name="name" 
                label="Name" 
                required 
                size="small" 
                fullWidth
                value={elementDetails?.data?.name ?? ''} 
                onChange={handleChange}
                inputProps={{ maxLength: 100 }}
              />
              
              <TextField 
                id="description" 
                name="description" 
                label="Description" 
                size="small"
                fullWidth
                multiline 
                rows={3} 
                value={elementDetails?.data?.description ?? ''} 
                onChange={handleChange}
                inputProps={{ maxLength: 500 }}
              />
              
              <TextField 
                id="url" 
                name="url" 
                label="Web page" 
                size="small" 
                fullWidth
                type='url' 
                value={elementDetails?.data?.url ?? ''} 
                onChange={handleChange}
                inputProps={{ maxLength: 256 }}
              />
              
              {type === "nodes" && (
                <Stack spacing={2.5}>
                  <Accordion
                    disableGutters
                    expanded={expandedAppearance === 'panelAppearancePerson'}
                    onChange={handleChangeAppearance('panelAppearancePerson')}
                    sx={accordionStyles}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel-person-content"
                      id="panel-person-header"
                      sx={{ px: 3 }}
                    >
                      <Typography variant="subtitle1">Person</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={accordionDetailsStyles}>
                      <Stack spacing={1.5}>
                        {personFields.map(field => (
                          <TextField
                            key={field.name}
                            size="small"
                            name={field.name}
                            label={field.label}
                            type="text"
                            fullWidth
                            value={elementDetails?.data?.[field.name] ?? ''}
                            onChange={handleChange}
                          />
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      bgcolor: 'background.paper'
                    }}
                  >
                    <ElementPropertyNodeImage 
                      projectId={effectiveProjectId} 
                      node={elementDetails} 
                      updateImage={updateElementImage} 
                      updateImageUrl={handleChange}
                    />
                  </Box>
                  
                  {/* <Accordion>
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />} 
                      aria-controls="panel2a-content" 
                      id="panel2a-header"
                    >
                      <Typography>Tags</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TagField 
                        selectedItem={selectedItem} 
                        elements={elements}
                      />
                    </AccordionDetails>
                  </Accordion> */}
                  
                  <Accordion
                    disableGutters
                    expanded={expandedAppearance === 'panelAppearanceOther'}
                    onChange={handleChangeAppearance('panelAppearanceOther')}
                    sx={accordionStyles}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />} 
                      aria-controls="panel-other-content" 
                      id="panel-other-header"
                      sx={{ px: 3 }}
                    >
                      <Typography variant="subtitle1">Other</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={accordionDetailsStyles}>
                      <Stack spacing={1.5}>
                        {customFields.map(field => (
                          <TextField
                            key={field.name}
                            size="small"
                            name={field.name}
                            label={field.label}
                            type="text"
                            fullWidth
                            value={elementDetails?.data?.[field.name] ?? ''}
                            onChange={handleChange}
                          />
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              )}
              
              {type === "edges" && (
                <Stack spacing={2}>
                  <SelectDefaultEdgeShape 
                    name="edgetype" 
                    handleChange={handleChange} 
                    value={elementDetails?.data?.edgetype} 
                    hideLabel={true} 
                  />
                  <SelectDefaultEdgeArrows 
                    name="edgearrows" 
                    handleChange={handleChange} 
                    value={elementDetails?.data?.edgearrows} 
                    hideLabel={true} 
                  />
                </Stack>
              )}
            </Stack>
          </TabPanel>
          
          <TabPanel value="2" sx={{ px: 0 }}>
            {type === "nodes" && (
              <Stack spacing={2.5}>
                <Accordion 
                  disableGutters
                  expanded={expandedAppearance === 'panelAppearance1'} 
                  onChange={handleChangeAppearance('panelAppearance1')} 
                  sx={accordionStyles}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel-node-appearance-content"
                    id="panel-node-appearance-header"
                    sx={{ px: 3 }}
                  >
                    <Typography variant="subtitle1" sx={{ flexShrink: 0 }}>Node Appearance</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={accordionDetailsStyles}>
                    <Stack spacing={2}>
                      <TextField
                        id="nodeshape"
                        select
                        label="Shape style"
                        size="small"
                        name="nodeshape"
                        value={elementDetails?.data?.nodeshape ?? ''}
                        onChange={handleChange}
                        sx={{ width: "100%" }}
                      >
                        {StyleShape.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      
                      <ColorPicker 
                        label="Background color" 
                        name="elementcolor" 
                        handleChange={handleChange} 
                        color={getElementColor}
                      />
                      
                      <ColorPicker 
                        label="Border color" 
                        name="bordercolor" 
                        handleChange={handleChange} 
                        color={getBorderColor}
                      />
                      
                      <TextField
                        id="borderwidth"
                        name="borderwidth"
                        label="Border Width"
                        type="number"
                        value={resolvedBorderWidth}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: 0, max: 10 } }}
                        sx={{ width: 160 }}
                        onChange={handleChange}
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>
                
                <Accordion 
                  disableGutters
                  expanded={expandedAppearance === 'panelAppearance3'} 
                  onChange={handleChangeAppearance('panelAppearance3')}
                  sx={accordionStyles}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel-text-settings-content"
                    id="panel-text-settings-header"
                    sx={{ px: 3 }}
                  >
                    <Typography variant="subtitle1">Text Settings</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={accordionDetailsStyles}>
                    <Stack spacing={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            name="textshowlabel"
                            checked={Boolean(getTextShowLabel)}
                            onChange={handleChange}
                          />
                        }
                        label={<Typography variant="body1">Show label</Typography>}
                      />
                      <ColorPicker 
                        label="Text color" 
                        name="textcolor" 
                        handleChange={handleChange} 
                        color={getTextColor}
                      />

                      <ColorPicker 
                        label="Text border color" 
                        name="textbordercolor" 
                        handleChange={handleChange} 
                        color={getTextBorderColor}
                      />
                      <TextField
                        id="textsize"
                        name="textsize"
                        label="Text Size"
                        type="number"
                        value={resolvedTextSize}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: 0, max: 100 } }}
                        sx={{ width: 160 }}
                        onChange={handleChange}
                      />
                      <TextField
                        id="textborderwidth"
                        name="textborderwidth"
                        label="Border Width"
                        type="number"
                        value={resolvedTextBorderWidth}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: 0, max: 10 } }}
                        sx={{ width: 160 }}
                        onChange={handleChange}
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            )}
            
            {type === "edges" && (
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="subtitle1" sx={{ flexShrink: 0 }}>Edge Appearance</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Settings here apply to the currently selected edge.
                  </Typography>
                </Box>
                <ColorPicker 
                  label="Edge color" 
                  name="edgecolor" 
                  handleChange={handleChange} 
                  color={getEdgeColor}
                />
              </Stack>
            )}
          </TabPanel>
        </TabContext>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          bgcolor: 'background.paper',
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <Button 
          variant="outlined" 
          color="inherit" 
          onClick={handleClose}
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="success" 
          onClick={handleSave}
          startIcon={<SaveIcon />}
          disabled={!elementDetails}
        >
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};